// web-commands.ts — the PER-APP half of the web shape: maps oriora_forever_paw's Tauri data
// commands (normally served by ipc.rs/db.rs over native SQLite) to the shared browser WebStore.
// Plain CRUD is a few lines each. The photo-save commands persist to a browser data URL (no fs);
// the derived command (compute_signals) returns a minimal shell for now — a faithful web build
// reimplements that signal logic in JS (the known per-app wrinkle, see MACOS/web docs).

import type { CommandHandler, WebStore, Row } from "./web-runtime";
import { computeSignalsWeb } from "./signals-web";
import type { Moment, Pet } from "./store";
import { browserSearch, browserFetch } from "./web-search-browser";

const PET = "pet";
const MOMENTS = "moments";
const CARE = "care";

function uuid(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
/** Mirrors Db::clean_kind — an unknown kind degrades to a note rather than losing the row. */
function cleanKind(raw: unknown): string {
  const k = String(raw ?? "").trim().toLowerCase();
  return k === "weight" || k === "vet" || k === "med" || k === "symptom" ? k : "note";
}
/** Rows written before 1.3.0 have no pet_id; the native migration backfills them, and the web
 *  store does the same by treating a blank pet_id as belonging to the first pet. */
function belongsTo(row: Record<string, unknown>, petId: unknown, firstId: string): boolean {
  if (!petId) return true;
  const owner = String(row.pet_id ?? "") || firstId;
  return owner === String(petId);
}
function nowIso(): string {
  return new Date().toISOString();
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    // ── Pet (single subject) ──
    // Multi-pet (1.3.0) — mirrors db.rs: an explicit id picks that pet, no id = the first.
    get_pet: ({ id }) => (id ? db.get(PET, String(id)) ?? null : db.all(PET)[0] ?? null),
    list_pets: () => db.all(PET).sort((a, b) => String(a.created_at).localeCompare(String(b.created_at))),
    create_pet: ({ patch }) => {
      const p = (patch ?? {}) as Record<string, unknown>;
      const now = nowIso();
      return db.insert(PET, {
        id: uuid(),
        name: String(p.name ?? "").trim(),
        kind: String(p.kind ?? "").trim().toLowerCase(),
        photo: String(p.photo ?? ""),
        birth_on: String(p.birth_on ?? ""),
        farewell_on: String(p.farewell_on ?? ""),
        created_at: now,
        updated_at: now,
      } as Row);
    },
    delete_pet: ({ id }) => {
      const pid = String(id);
      for (const r of db.all(MOMENTS)) if (String(r.pet_id ?? "") === pid) db.remove(MOMENTS, String(r.id));
      for (const r of db.all(CARE)) if (String(r.pet_id ?? "") === pid) db.remove(CARE, String(r.id));
      db.remove(PET, pid);
      return null;
    },
    upsert_pet: ({ id, patch }) => {
      const p = (patch ?? {}) as Record<string, unknown>;
      const now = nowIso();
      const existing = id ? db.get(PET, String(id)) : db.all(PET)[0];
      if (existing) {
        const next: Record<string, unknown> = { updated_at: now };
        for (const k of ["name", "kind", "photo", "birth_on", "farewell_on"]) if (k in p) next[k] = p[k];
        db.update(PET, existing.id, next);
        return db.get(PET, existing.id);
      }
      const row: Row = {
        id: uuid(),
        name: String(p.name ?? ""),
        kind: String(p.kind ?? ""),
        photo: String(p.photo ?? ""),
        birth_on: String(p.birth_on ?? ""),
        farewell_on: String(p.farewell_on ?? ""),
        created_at: now,
        updated_at: now,
      };
      return db.insert(PET, row);
    },

    // ── Care log (mirrors db.rs care CRUD) ──
    list_care: ({ petId, kind, limit }) => {
      const firstId = String(db.all(PET)[0]?.id ?? "");
      let rows = (db.all(CARE) as Array<Record<string, unknown>>)
        .filter((r) => belongsTo(r, petId, firstId)) as Array<{ id: string; kind?: string; happened_on?: string; created_at?: string }>;
      if (kind) rows = rows.filter((r) => String(r.kind ?? "") === String(kind));
      rows.sort((a, b) => {
        const s = String(b.happened_on ?? "").localeCompare(String(a.happened_on ?? ""));
        return s !== 0 ? s : String(b.created_at ?? "").localeCompare(String(a.created_at ?? ""));
      });
      const lim = typeof limit === "number" ? limit : Number(limit ?? 50000);
      return rows.slice(0, lim);
    },
    get_care: ({ id }) => db.get(CARE, String(id)),
    create_care: ({ petId, input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const now = nowIso();
      const kind = cleanKind(i.kind);
      const row: Row = {
        id: uuid(),
        happened_on: String(i.happened_on ?? new Date().toISOString().slice(0, 10)),
        kind,
        pet_id: String(petId ?? db.all(PET)[0]?.id ?? ""),
        due_on: String(i.due_on ?? ""),
        title: String(i.title ?? "").trim(),
        detail: String(i.detail ?? "").trim(),
        // Mirrors the Rust rule: a number only means something on a weight row.
        value_num: kind === "weight" && typeof i.value_num === "number" ? i.value_num : null,
        created_at: now,
        updated_at: now,
      };
      return db.insert(CARE, row);
    },
    update_care: ({ id, patch }) => {
      const p = (patch ?? {}) as Record<string, unknown>;
      const existing = db.get(CARE, String(id)) as Record<string, unknown> | undefined;
      if (!existing) return null;
      const kind = "kind" in p ? cleanKind(p.kind) : String(existing.kind ?? "note");
      const next: Record<string, unknown> = { updated_at: nowIso(), kind };
      for (const k of ["happened_on", "title", "detail", "due_on"]) if (k in p) next[k] = String(p[k] ?? "").trim();
      next.value_num =
        kind === "weight"
          ? typeof p.value_num === "number"
            ? p.value_num
            : (existing.value_num ?? null)
          : null;
      db.update(CARE, String(id), next);
      return null;
    },
    delete_care: ({ id }) => {
      db.remove(CARE, String(id));
      return null;
    },

    // ── Moments ──
    list_moments: ({ petId, from, to, limit }) => {
      const firstId = String(db.all(PET)[0]?.id ?? "");
      let rows = (db.all(MOMENTS) as Array<Record<string, unknown>>)
        .filter((r) => belongsTo(r, petId, firstId)) as Array<{ id: string; happened_on?: string; created_at?: string }>;
      if (from) rows = rows.filter((r) => String(r.happened_on ?? "") >= String(from));
      if (to) rows = rows.filter((r) => String(r.happened_on ?? "") <= String(to));
      rows.sort((a, b) => {
        const s = String(b.happened_on ?? "").localeCompare(String(a.happened_on ?? ""));
        return s !== 0 ? s : String(b.created_at ?? "").localeCompare(String(a.created_at ?? ""));
      });
      const lim = typeof limit === "number" ? limit : Number(limit ?? 50000);
      return rows.slice(0, lim);
    },
    get_moment: ({ id }) => db.get(MOMENTS, String(id)),
    create_moment: ({ petId, input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const now = nowIso();
      const row: Row = {
        id: uuid(),
        happened_on: String(i.happened_on ?? now.slice(0, 10)),
        title: String(i.title ?? ""),
        caption: String(i.caption ?? ""),
        photo: String(i.photo ?? ""),
        place: String(i.place ?? "").trim().toLowerCase(),
        is_milestone: Boolean(i.is_milestone ?? false),
        pet_id: String(petId ?? db.all(PET)[0]?.id ?? ""),
        created_at: now,
        updated_at: now,
      };
      return db.insert(MOMENTS, row);
    },
    update_moment: ({ id, patch }) => {
      const p = (patch ?? {}) as Record<string, unknown>;
      const next: Record<string, unknown> = { updated_at: nowIso() };
      for (const k of ["happened_on", "title", "caption", "photo", "place", "is_milestone"]) {
        if (k in p) next[k] = k === "place" ? String(p[k] ?? "").trim().toLowerCase() : p[k];
      }
      db.update(MOMENTS, String(id), next);
      return null;
    },
    delete_moment: ({ id }) => {
      db.remove(MOMENTS, String(id));
      return null;
    },

    // ── Photos — no filesystem on web; return a self-contained ref the <img> can render. ──
    save_photo_from_path: ({ srcPath }) => String(srcPath ?? ""),
    save_photo_bytes: ({ bytes, ext }) => {
      const arr = Uint8Array.from((bytes as number[]) ?? []);
      let bin = "";
      for (let n = 0; n < arr.length; n++) bin += String.fromCharCode(arr[n]);
      const kind = String(ext ?? "png").toLowerCase() || "png";
      return `data:image/${kind};base64,${btoa(bin)}`;
    },

    // ── Derived — Rust in signals.rs, faithfully ported for web in signals-web.ts.
    // This used to be a valid-but-EMPTY shell, so on web the MemoryInsights card showed zeros over
    // a book that clearly had moments in it, and the BILLED remembrance was handed an empty life.
    // ⚠️ Multi-pet: scope BOTH the pet and its moments. Unscoped, the web build would hand the
    // BILLED story call every animal's moments at once and narrate them as a single life.
    compute_signals: ({ today, focus, petId }) => computeSignalsWeb(
      ((petId ? db.get(PET, String(petId)) : db.all(PET)[0]) ?? null) as unknown as Pet | null,
      (() => {
        const firstId = String(db.all(PET)[0]?.id ?? "");
        const scope = petId ?? firstId;
        return (db.all(MOMENTS) as Array<Record<string, unknown>>)
          .filter((r) => belongsTo(r, scope, firstId)) as unknown as Moment[];
      })(),
      String(today ?? new Date().toISOString().slice(0, 10)),
      (focus ?? null) as { kind: string; value: string } | null,
    ),
  };
}
